import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { CheckBoxSizes } from '@shared/v2';

/**
 * Компонент `SdvorCheckboxComponent` используется для выбора одного или нескольких элементов
 * из набора опций. Он представляет собой интерактивный элемент пользовательского
 * интерфейса, который может быть установлен (выбран) или снят (не выбран).
 *
 * ```ts
 * import { SdvorCheckboxComponent } from '@sdvor/shared/ui/v2';
 * ```
 */
@Component({
  selector: 'sd-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SdvorCheckboxComponent),
      multi: true,
    },
  ],
})
export class SdvorCheckboxComponent implements ControlValueAccessor {
  // TODO проверить насколько необходимо это свойство
  /** Наименование элемента */
  @Input() name: string;
  /** Установлен ли чекбокс */
  @Input() checked: boolean;
  /** Состояние деактивации */
  @Input() @HostBinding('class.disabled') disabled = false;
  /** Событие, которое срабатывает при изменении состояния чекбокса */
  @Output() changed = new EventEmitter<boolean>();

  @Input() size: CheckBoxSizes;

  /** @ignore */
  private onChange: (_: any) => void;
  /** @ignore */
  private onTouched: (_: any) => void;

  constructor(private cdr: ChangeDetectorRef) {}

  /** @ignore */
  writeValue(value: boolean): void {
    this.checked = value;
    this.cdr.markForCheck();
  }

  /** @ignore */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /** @ignore */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /** @ignore */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** @ignore */
  inputOnChange(value: boolean): void {
    this.changed.emit(value);

    if (this.onChange) {
      this.onChange(value);
    }
    if (this.onTouched) {
      this.onTouched(value);
    }
  }

  @HostBinding('class')
  get classNames(): string[] {
    return ['checkbox', this.size ? 'checkbox-size-' + this.size : ''].filter(Boolean);
  }
}
